import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserSessionDetail } from '../../interface/user.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private _userDetail: UserSessionDetail | null = null;
  private _refreshPromise: Promise<boolean> | null = null;
  private readonly _storageKey = 'userInfo';
  private readonly _refreshSkewSeconds = 60;

  constructor() {}

  setUserDetail(userDetail: Partial<UserSessionDetail>): void {
    const existingDetail: UserSessionDetail = this._userDetail || ({} as UserSessionDetail);
    const incomingDetail: Partial<UserSessionDetail> = userDetail || {};

    const mergedUser = {
      ...(existingDetail.user || {}),
      ...(incomingDetail.user || {}),
    };

    const mergedDetail = {
      ...existingDetail,
      ...incomingDetail,
      ...(Object.keys(mergedUser).length ? { user: mergedUser } : {}),
    } as UserSessionDetail;

    if (!mergedDetail.expires_at && mergedDetail.expires_in) {
      mergedDetail.expires_at = Math.floor(Date.now() / 1000) + Number(mergedDetail.expires_in);
    }

    this._userDetail = mergedDetail;
    this._persistUserDetail(mergedDetail);
  }

  getUserDetail(): UserSessionDetail | null {
    if (!this._userDetail) {
      this._userDetail = this._readStoredUserDetail();
    }
    return this._userDetail;
  }

  clearUserDetail(): void {
    this._userDetail = null;
    localStorage.removeItem(this._storageKey);
    sessionStorage.removeItem(this._storageKey);
  }

  async ensureValidSession(forceRefresh = false): Promise<boolean> {
    const userSessionInfo = this.getUserDetail();
    if (!userSessionInfo) {
      return false;
    }

    if (!forceRefresh && this._isAccessTokenValid(userSessionInfo)) {
      return true;
    }

    if (!userSessionInfo.refresh_token) {
      return this._isAccessTokenValid(userSessionInfo);
    }

    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = this._refreshWithToken(userSessionInfo.refresh_token);
    try {
      return await this._refreshPromise;
    } finally {
      this._refreshPromise = null;
    }
  }

  isAuthenticated(): boolean {
    const userSessionInfo = this.getUserDetail();
    if (!userSessionInfo) {
      return false;
    }

    return this._isAccessTokenValid(userSessionInfo) || !!userSessionInfo.refresh_token;
  }

  get getAuthToken(): string {
    const userSessionInfo = this.getUserDetail();
    return userSessionInfo?.access_token ? `Bearer ${userSessionInfo.access_token}` : '';
  }

  get getApiKey(): string {
    return environment.apiKey;
  }

  fetchUserProfile(): Observable<UserSessionDetail | null> {
    return new Observable<UserSessionDetail | null>(observer => {
      (async () => {
        const hasValidSession = await this.ensureValidSession();
        if (!hasValidSession) {
          observer.next(null);
          observer.complete();
          return;
        }

        const userSessionInfo = this.getUserDetail();
        if (!userSessionInfo?.access_token) {
          observer.next(null);
          observer.complete();
          return;
        }

        try {
          const response = await fetch(`${environment.apiUrl}/auth/v1/user`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${userSessionInfo.access_token}`,
              apikey: this.getApiKey,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const user = await response.json();
          const enrichedUserData: UserSessionDetail = {
            ...userSessionInfo,
            user,
          };
          this.setUserDetail(enrichedUserData);
          observer.next(enrichedUserData);
          observer.complete();
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          observer.next(null);
          observer.complete();
        }
      })();
    });
  }

  private _persistUserDetail(userDetail: UserSessionDetail): void {
    const serialized = JSON.stringify(userDetail);
    localStorage.setItem(this._storageKey, serialized);
    sessionStorage.setItem(this._storageKey, serialized);
  }

  private _readStoredUserDetail(): UserSessionDetail | null {
    const localStored = localStorage.getItem(this._storageKey);
    if (localStored) {
      try {
        const parsed = JSON.parse(localStored) as UserSessionDetail;
        sessionStorage.setItem(this._storageKey, localStored);
        return parsed;
      } catch {
        localStorage.removeItem(this._storageKey);
      }
    }

    const sessionStored = sessionStorage.getItem(this._storageKey);
    if (!sessionStored) {
      return null;
    }

    try {
      const parsed = JSON.parse(sessionStored) as UserSessionDetail;
      localStorage.setItem(this._storageKey, sessionStored);
      return parsed;
    } catch {
      sessionStorage.removeItem(this._storageKey);
      return null;
    }
  }

  private _isAccessTokenValid(userSessionInfo: UserSessionDetail): boolean {
    if (!userSessionInfo.access_token) {
      return false;
    }

    if (!userSessionInfo.expires_at) {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    return userSessionInfo.expires_at - now > this._refreshSkewSeconds;
  }

  private async _refreshWithToken(refreshToken: string): Promise<boolean> {
    try {
      const response = await fetch(`${environment.apiUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          apikey: this.getApiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        this.clearUserDetail();
        return false;
      }

      const refreshed = await response.json();
      if (!refreshed?.access_token) {
        this.clearUserDetail();
        return false;
      }

      this.setUserDetail(refreshed as Partial<UserSessionDetail>);
      return true;
    } catch (error) {
      console.error('Failed to refresh session:', error);
      return false;
    }
  }
}
